// @flow
import {
  call,
  takeEvery,
  takeLatest,
  put,
  select,
  race,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { normalize } from 'normalizr';
import i18n from 'i18n-js';

import { arrayOfBuses } from '../api/schemas/buses';
import * as types from '../types/buses';
import { TIME_UPDATED } from '../types/time';
import * as selectors from '../reducers';
import { REQUEST_TIMEOUT, DEFAULT_LOCATION } from '../settings';
import { throwTimeout } from '../lib/http';
import * as busesActions from '../actions/buses';
import * as closeableActions from '../actions/closeable';
import { Buses, School } from '../api';
import HTTP_STATUS from '../lib/HTTPStatus';
import * as temporalBannersActions from '../actions/temporalBanners';
import {
  DEFAULT_CREATE,
  DEFAULT_DURATION,
  FETCH_ERROR,
  CREATE_ERROR,
  UPDATE_ERROR,
  REMOVE_ERROR,
  DEFAULT_REMOVE,
  DEFAULT_UPDATE,
  DEFAULT_ERROR,
} from '../utils/errorDefaults';
import { OFFLINE_BUS_TIMEOUT } from '../settings';


function* fetchBus(action) {
  const token = yield select(selectors.getToken);
  const id = action.payload;

  try {
    // Fetch the next route
    const { busResponse, timeout } = yield race({
      busResponse: call(
        [Buses, 'detail'],
        { id, token },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchBus saga');
    }
    const trip = yield select(selectors.getTrip, busResponse.trip);
    let location = DEFAULT_LOCATION;
    if (trip) {
      location = trip.last_position;
    }
    const oldBus = yield select(selectors.getBus, id);
    if (oldBus) {
      location = { ...oldBus.location };
    }
    yield put(busesActions.fetchBusSucceed({
      ...busResponse,
      location,
    }));
  } catch (error) {
    // Set error
    yield put(temporalBannersActions.shout(FETCH_ERROR(i18n.t('common.bus', { count: 1 })), DEFAULT_DURATION, -1, {}));
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(busesActions.fetchBusFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
      object_id: id,
    }));
  }
}

function* fetchSchoolBuses(action) {
  yield delay(400);
  const token = yield select(selectors.getToken);
  const filter = yield select(selectors.getBusFilter);
  const profile = yield select(selectors.getProfileOfType, 'admin');
  try {
    // Fetch the next route
    const { bussesResponse, timeout } = yield race({
      bussesResponse: call(
        [School.custom, 'buses'],
        { id: profile.school, token, filters: { name: filter || '' } },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchBus saga');
    }
    const {
      entities,
      result,
    } = normalize(bussesResponse, arrayOfBuses);
    yield put(busesActions.fetchSchoolBusesSuccess(entities.buses, result));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    if (statusCode !== 500) {
      yield put(temporalBannersActions.shout(FETCH_ERROR(i18n.t('common.bus', { count: 2 })), DEFAULT_DURATION, -1, {}));
    }

    yield put(busesActions.fetchBusFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* removeBus(action) {
  const id = action.payload;
  const token = yield select(selectors.getToken);
  try {
    const { timeout } = yield race({
      response: call(
        [Buses, 'remove'],
        { token, id },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });
    if (timeout) {
      throwTimeout('delete bus saga');
    }
    yield put(busesActions.busDeleteSuccess(id));
    yield put(temporalBannersActions.shout(DEFAULT_REMOVE(i18n.t('common.bus', { count: 1 })), DEFAULT_DURATION, 1, {}));
  } catch (error) {
    yield put(temporalBannersActions.shout(REMOVE_ERROR(i18n.t('common.bus', { count: 1 })), DEFAULT_DURATION, -1, {}));
    const {
      statusCode, message, data, isPlain,
    } = error;
    yield put(busesActions.busDeleteFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* createBus(action) {
  const token = yield select(selectors.getToken);
  const busData = action.payload;
  const profile = yield select(selectors.getProfileOfType, 'admin');
  try {
    const { busResponse, timeout } = yield race({
      busResponse: call(
        [Buses, 'create'],
        {
          token,
          data:
            {
              ...busData,
              school: profile.school,
            },
        },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchBus saga');
    }
    yield put(temporalBannersActions.shout(DEFAULT_CREATE(i18n.t('common.bus', { count: 1 })), DEFAULT_DURATION, 1, {}));
    yield put(busesActions.busCreateSuccess(busResponse));
    yield put(closeableActions.close('createBus'));
  } catch (error) {
    yield put(temporalBannersActions.shout(CREATE_ERROR(i18n.t('common.bus', { count: 1 })), DEFAULT_DURATION, -1, {}));
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(busesActions.busCreateFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* updateBus(action) {
  const token = yield select(selectors.getToken);
  const { id } = action.payload;
  try {
    const { busResponse, timeout } = yield race({
      busResponse: call(
        [Buses, 'update'],
        {
          token,
          id,
          data: {
            ...action.payload,
          },
        },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });
    if (timeout) {
      throwTimeout('updateBus saga');
    }
    yield put(busesActions.updateBusSucceed(busResponse));
    yield put(temporalBannersActions.shout(DEFAULT_UPDATE(i18n.t('common.bus', { count: 1 })), DEFAULT_DURATION, 1, {}));
  } catch (error) {
    yield put(temporalBannersActions.shout(UPDATE_ERROR(i18n.t('common.bus', { count: 1 })), DEFAULT_DURATION, -1, {}));
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(busesActions.updateBusFailed({
      statusCode,
      message: statusCode === HTTP_STATUS.NOT_FOUND ? CREATE_ERROR(i18n.t('common.bus', { count: 1 })) : message,
      data: isPlain ? DEFAULT_ERROR : data,
      retryAction: action,
    }));
  }
}

function* updateBusLastConnection({ payload }) {
  yield put(
    busesActions.updateBusLastConnection(payload.id, Date.now(), false),
  );
}

function* checkBusOfflineStatus(_action) {
  const trips = yield select(selectors.getTrips);

  for (let i = 0; i < trips.length; i++) {
    const trip = trips[i];

    const bus = yield select(selectors.getBus, trip.bus);

    if (Date.now() - bus.lastConnection > OFFLINE_BUS_TIMEOUT) {
      yield put(
        busesActions.updateBusLastConnection(bus.id, bus.lastConnection, true),
      );
    }
  }
}

export function* watchBusesFetch(): Iterator<any> {
  yield takeEvery(
    types.BUS_FETCH_STARTED,
    fetchBus,
  );
}

export function* watchCreateBus(): Iterator<any> {
  yield takeEvery(
    types.BUS_CREATE_STARTED,
    createBus,
  );
}

export function* watchRemoveBus(): Iterator<any> {
  yield takeEvery(
    types.DELETE_BUS_STARTED,
    removeBus,
  );
}

export function* watchFetchSchoolBuses(): Iterator<any> {
  yield takeLatest(
    types.FETCH_SCHOOl_BUSES_STARTED,
    fetchSchoolBuses,
  );
}

export function* watchUpdateBus(): Iterator<any> {
  yield takeLatest(
    types.UPDATE_BUS_STARTED,
    updateBus,
  );
}

export function* watchUpdateBusLastPosition(): Iterator<any> {
  yield takeLatest(
    types.BUS_UPDATED,
    updateBusLastConnection,
  );
}

export function* watchTimeUpdatedBusOffline(): Iterator<any> {
  yield takeLatest(
    TIME_UPDATED,
    checkBusOfflineStatus,
  );
}



// WEBPACK FOOTER //
// ./src/scripts/sagas/buses.js